import { useMemo, useState } from "react";
import { GoogleMap, InfoWindowF, MarkerF, PolygonF, useJsApiLoader } from "@react-google-maps/api";
import { Box, Center, Loader, Stack, Text } from "@mantine/core";
import { useComputedColorScheme } from "@mantine/core";
import type { LiveDriver, LiveSos, LiveTrip } from "../../api/console";
import type { DemandCell, HexGrid } from "../../lib/hexgrid";
import { GOOGLE_MAPS_API_KEY } from "../../config/env";
import { seqStep, tokensFor, type TokenName } from "../../theme/tokens";
import { useI18n } from "../../i18n/context";
import { vehicleImage } from "../brand/VehicleIcon";

// Dar es Salaam city centre.
const DAR = { lat: -6.7924, lng: 39.2083 };

// Marker colours carry state (spec: operational states), but every marker also
// has a distinct SHAPE (circle = driver halo, vehicle art on top, arrow pin =
// trip pickup, big labelled disc = SOS) and a title, so colour is never the
// only cue.
export const MAP_TOKENS = {
  idle: "success-solid",
  assigned: "warning-solid",
  onTrip: "accent-driver-solid",
  stale: "text-disabled",
  searching: "info-solid",
  stuck: "error-solid",
  sos: "error-solid",
} as const satisfies Record<string, TokenName>;

// Google map styling from the neutral ramp (gunmetal-tinted, never pure grey).
const DARK_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#1E2223" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#939B9C" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#141718" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#2E3435" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3B4243" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0F1112" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];
const LIGHT_STYLE = [
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#F4F6F6" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#C2DFE3" }] },
];

// SymbolPath.CIRCLE / FORWARD_CLOSED_ARROW as numbers so we don't need the
// google.maps type package at compile time.
const CIRCLE = 0;
const PIN = 3;

export type Selection =
  | { kind: "trip"; id: string }
  | { kind: "driver"; id: string }
  | { kind: "sos"; id: string }
  | { kind: "cell"; key: string }
  | null;

export function LiveMap({
  drivers,
  trips,
  sos,
  cells,
  grid,
  showDemand,
  selection,
  onSelect,
  height = 560,
}: {
  drivers: LiveDriver[];
  trips: LiveTrip[];
  sos: LiveSos[];
  cells: DemandCell[];
  grid: HexGrid;
  showDemand: boolean;
  selection: Selection;
  onSelect: (s: Selection) => void;
  height?: number | string;
}) {
  const { t } = useI18n();
  const scheme = useComputedColorScheme("light");
  const { isLoaded, loadError } = useJsApiLoader({
    id: "safiri-admin-maps",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });
  const [map, setMap] = useState<unknown>(null);

  const maxPressure = useMemo(() => Math.max(1, ...cells.map((c) => c.pressure)), [cells]);
  // Google Maps can't read CSS variables — resolve the tokens for this theme.
  const tk = useMemo(() => tokensFor(scheme), [scheme]);
  const ring = tk["surface-canvas"];

  if (loadError) {
    return (
      <Center h={height}>
        <Text c="var(--error-fg)">{t("live.mapError")}</Text>
      </Center>
    );
  }
  if (!isLoaded) {
    return (
      <Center h={height}>
        <Loader />
      </Center>
    );
  }

  const selectedCell = selection?.kind === "cell" ? cells.find((c) => c.key === selection.key) : null;

  return (
    <Box h={height} style={{ borderRadius: 12, overflow: "hidden" }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={DAR}
        zoom={12}
        onLoad={(m) => setMap(m)}
        onUnmount={() => setMap(null)}
        options={{
          styles: scheme === "dark" ? DARK_STYLE : LIGHT_STYLE,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          clickableIcons: false,
          gestureHandling: "greedy",
        }}
      >
        {map !== null && showDemand &&
          cells.slice(0, 250).map((c) => {
            const intensity = Math.min(1, c.pressure / maxPressure);
            // Demand heat on the oxblood sequential ramp; cells that left riders
            // unserved get an error-solid edge on top of their heat.
            const fill = tk[seqStep(intensity) ?? "viz-seq-1"];
            const stroke = c.unserved > 0 ? tk["error-solid"] : tk["viz-seq-4"];
            return (
              <PolygonF
                key={c.key}
                paths={grid.polygon(c.hex)}
                options={{
                  fillColor: fill,
                  fillOpacity: 0.55,
                  strokeColor: stroke,
                  strokeOpacity: 0.9,
                  strokeWeight: selection?.kind === "cell" && selection.key === c.key ? 3 : c.unserved > 0 ? 2 : 1,
                }}
                onClick={() => onSelect({ kind: "cell", key: c.key })}
              />
            );
          })}

        {map !== null &&
          drivers
            .filter((d) => d.position)
            .flatMap((d) => {
              const selected = selection?.kind === "driver" && selection.id === d.id;
              const statusToken = d.stale ? MAP_TOKENS.stale : MAP_TOKENS[d.state];
              const statusLabel = d.stale
                ? t("live.stale")
                : d.state === "idle"
                  ? t("live.idle")
                  : d.state === "assigned"
                    ? t("live.assigned")
                    : t("live.busy");
              const title = `${d.name ?? "Driver"} · ${d.plate ?? ""} · ${statusLabel}`;
              const size = selected ? 30 : 24;
              return [
                // Status halo, sits behind the vehicle art.
                <MarkerF
                  key={`d-${d.id}-halo`}
                  position={d.position!}
                  title={title}
                  zIndex={d.state === "idle" ? 1 : 2}
                  onClick={() => onSelect({ kind: "driver", id: d.id })}
                  icon={{
                    path: CIRCLE,
                    scale: selected ? 17 : 14,
                    fillColor: tk[statusToken],
                    fillOpacity: showDemand ? 0.65 : 0.95,
                    strokeColor: ring,
                    strokeWeight: 2,
                  } as never}
                />,
                // Vehicle artwork on top, keyed by product (boda/bajaji/car).
                <MarkerF
                  key={`d-${d.id}-icon`}
                  position={d.position!}
                  title={title}
                  zIndex={d.state === "idle" ? 3 : 4}
                  onClick={() => onSelect({ kind: "driver", id: d.id })}
                  icon={{
                    url: vehicleImage(d.rideType, null),
                    scaledSize: { width: size, height: size * 0.62 },
                    anchor: { x: size / 2, y: (size * 0.62) / 2 },
                  } as never}
                />,
              ];
            })}

        {map !== null &&
          trips
            .filter((tr) => tr.pickup?.lat !== undefined && (tr.status === "searching" || tr.stuck))
            .map((tr) => (
              <MarkerF
                key={`t-${tr.id}`}
                position={{ lat: tr.pickup!.lat!, lng: tr.pickup!.lng! }}
                title={`${tr.ref ? `T-${tr.ref}` : tr.id.slice(-6)} · ${tr.stuck ?? t("live.searching")}`}
                zIndex={5}
                onClick={() => onSelect({ kind: "trip", id: tr.id })}
                icon={{
                  path: PIN,
                  scale: 6,
                  fillColor: tk[tr.stuck ? MAP_TOKENS.stuck : MAP_TOKENS.searching],
                  fillOpacity: 1,
                  strokeColor: ring,
                  strokeWeight: 2,
                } as never}
              />
            ))}

        {map !== null &&
          sos
            .filter((s) => s.position)
            .map((s) => (
              <MarkerF
                key={`s-${s.id}`}
                position={s.position!}
                title={`SOS · ${s.name}`}
                zIndex={10}
                label={{ text: "SOS", color: "#fff", fontSize: "10px", fontWeight: "700" }}
                onClick={() => onSelect({ kind: "sos", id: s.id })}
                icon={{
                  path: CIRCLE,
                  scale: 14,
                  fillColor: tk[MAP_TOKENS.sos],
                  fillOpacity: 1,
                  strokeColor: ring,
                  strokeWeight: 2,
                } as never}
              />
            ))}

        {selectedCell && (
          <InfoWindowF position={selectedCell.center} onCloseClick={() => onSelect(null)}>
            <Stack gap={2} c="#1E2223">
              <Text size="sm" fw={600}>
                {t("live.cell.title")}
              </Text>
              <Text size="xs">{t("live.cell.requests", { n: selectedCell.requests })}</Text>
              <Text size="xs">{t("live.cell.unserved", { n: selectedCell.unserved })}</Text>
              <Text size="xs">{t("live.cell.idle", { n: selectedCell.idleDrivers })}</Text>
            </Stack>
          </InfoWindowF>
        )}
      </GoogleMap>
    </Box>
  );
}
